import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, from, of } from 'rxjs';
import { environment as ENV } from '../../environments/environment';
import {
    IEmployeeCardProps, IEmployeeCard, IStaff,
    IEmployeeCardListItem, IEmployeeCardFilter, IEmployeeCardEditModel
} from '../models/EmployeeCard';
import { Utils } from '../core/Utils';
import { IPagingParams } from '../models/Common';
import { map, tap } from 'rxjs/operators';
import { TemplateService } from './template.service';

const cache: {
    cardProps?: IEmployeeCardProps,
    frontDataUrl?: string,
    backDataUrl?: string,
    [id: number]: {
        frontDataUrl?: string,
        backDataUrl?: string,
        photoDataUrl?: string
    }
} = {};

/**
 * Employee card service
 */
@Injectable({ providedIn: "root" })
export class EmployeeCardService {
    constructor(private http: HttpClient, private templateService: TemplateService) {
        this.templateService.getChanges().subscribe(() => {
            cache.frontDataUrl = undefined;
            cache.backDataUrl = undefined;
        });
    }

    public get apiUrl(): string { return `${ENV.apiUrl}/employeeCards` }

    /**
     * Get employee cards.
     * @param paging
     * @param filter
     * @returns
     */
    get(paging?: IPagingParams, filter?: IEmployeeCardFilter): Observable<{ items: IEmployeeCardListItem[], total: number }> {
        return this.http.get<{ items: IEmployeeCardListItem[], total: number }>(this.apiUrl, {
            params: Utils.sanitizeQueryParams({ ...filter, ...paging })
        });
    }

    /**
     * Get employee card details.
     * @param id
     * @returns
     */
    getById(id: number): Observable<IEmployeeCard> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.get<IEmployeeCard>(url);
    }

    /**
     * Get staff data by UPN. If UPN is not specified, current user data will be returned.
     * @param upn
     * @returns
     */
    getStaff(upn?: string): Observable<IStaff> {
        const url = `${this.apiUrl}/staff`;
        const params = Utils.sanitizeQueryParams({ upn });

        return this.http.get<IStaff>(url, { params });
    }

    /**
     * Get default card properties.
     * @returns
     */
    getCardProps(): Observable<IEmployeeCardProps> {
        if (cache.cardProps) return of(cache.cardProps);

        const url = `${this.apiUrl}/cardProps`;

        return this.http.get<IEmployeeCardProps>(url).pipe(tap(data => {
            cache.cardProps = data;
        }));
    }

    /**
     * Get URL of the employee card front side template.
     * @param id
     * @returns
     */
    getFrontTemplateUrl(): string {
        return `${this.apiUrl}/templates/front`;
    }

    /**
     * Get image data URL of the employee card front side template (cached).
     * @returns
     */
    getFrontTemplateDataUrl(): Observable<string> {
        return this.getTemplateImageDataUrlCached('frontDataUrl', this.getFrontTemplateUrl());
    }

    /**
     * Get URL of the employee card back side template.
     * @param id
     * @returns
     */
    getBackTemplateUrl(): string {
        return `${this.apiUrl}/templates/back`;
    }

    /**
     * Get image data URL of the employee card back side template (cached).
     * @returns
     */
    getBackTemplateDataUrl(): Observable<string> {
        return this.getTemplateImageDataUrlCached('backDataUrl', this.getBackTemplateUrl());
    }

    /**
     * Get URL of the employee card photo.
     * @param id
     * @returns
     */
    getPhotoUrl(id: number): string {
        return `${this.apiUrl}/${id}/photo`;
    }

    /**
     * Get image data URL of the employee card photo (cached).
     * @param id
     * @returns
     */
    getPhotoDataUrl(id: number): Observable<string> {
        return this.getCardImageDataUrlCached(id, 'photoDataUrl', this.getPhotoUrl(id));
    }

    /**
     * Get URL of the employee card front side.
     * @param id
     * @returns
     */
    getFrontUrl(id: number): string {
        return `${this.apiUrl}/${id}/front`;
    }

    /**
     * Get image data URL of the employee card front side (cached).
     * @param id
     * @returns
     */
    getFrontDataUrl(id: number): Observable<string> {
        return this.getCardImageDataUrlCached(id, 'frontDataUrl', this.getFrontUrl(id));
    }

    /**
     * Get URL of the employee card back side.
     * @param id
     * @returns
     */
    getBackUrl(id: number): string {
        return `${this.apiUrl}/${id}/back`;
    }

    /**
     * Get image data URL of the employee card back side (cached).
     * @param id
     * @returns
     */
    getBackDataUrl(id: number): Observable<string> {
        return this.getCardImageDataUrlCached(id, 'backDataUrl', this.getBackUrl(id));
    }

    /**
     * Get URL of the employee card document.
     * @param id
     * @returns
     */
    getDocumentUrl(id: number): string {
        return `${this.apiUrl}/${id}/document`;
    }

    /**
     * Create new employee card request.
     * @param data
     * @param upn
     * @returns
     */
    create(data: IEmployeeCardEditModel, upn?: string): Observable<number> {
        const form = new FormData();
        form.append('photo', data.Photo, data.Photo.name);

        const json = { ...data };
        delete json.Photo;

        form.append('data', JSON.stringify(json));

        const params = Utils.sanitizeQueryParams({ upn });

        return this.http.post<number>(this.apiUrl, form, { params });
    }

    /**
     * Update employee card.
     * @param id
     * @param data
     * @returns
     */
    update(id: number, data: IEmployeeCardEditModel): Observable<any> {
        const url = `${this.apiUrl}/${id}`;

        const form = new FormData();

        if (data.Photo) {
            form.append('photo', data.Photo, data.Photo.name);
        }

        const json = { ...data };
        delete json.Photo;

        form.append('data', JSON.stringify(json));

        return this.http.put<any>(url, form).pipe(tap(() => {
            cache[id] = {};
        }));
    }

    /**
     * Delete employee card.
     * @param id
     * @returns
     */
    delete(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}`;

        return this.http.delete<any>(url).pipe(tap(() => {
            cache[id] = {};
        }));
    }

    /**
     * Submit employee card for approval.
     * @param id
     * @returns
     */
    submit(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}/submit`;
        return this.http.put<any>(url, null);
    }

    /**
     * Revoke employee card.
     * @param id
     * @param reasonId
     * @returns
     */
    revoke(id: number, reasonId: string): Observable<any> {
        const url = `${this.apiUrl}/${id}/revoke`;
        return this.http.put<any>(url, { reasonId });
    }

    /**
     * Approve employee card.
     * @param id
     * @returns
     */
    approve(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}/approve`;
        return this.http.put<any>(url, null);
    }

    /**
     * Reject employee card.
     * @param id
     * @returns
     */
    reject(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}/reject`;
        return this.http.put<any>(url, null);
    }

    /**
     * Download employee card document.
     * @param id
     * @returns
     */
    download(id: number): Observable<any> {
        const url = this.getDocumentUrl(id);
        return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(map(response => {
            Utils.downloadResponseFile(response);
            return of(undefined);
        }));
    }

    private getTemplateImageDataUrlCached(key: 'frontDataUrl' | 'backDataUrl', rawUrl: string): Observable<string> {
        if (cache[key]) return of(cache[key]);

        const subj = new Subject<string>();

        Utils.imageUrlToDataUrl(rawUrl).then(dataUrl => {
            cache[key] = dataUrl;
            subj.next(dataUrl);
        });

        return subj.asObservable();
    }

    private getCardImageDataUrlCached(id: number, key: keyof typeof cache[number], rawUrl: string): Observable<string> {
        const promise = new Promise<string>((resolve) => {
            if (cache[id]?.[key]) {
                resolve(cache[id][key]);
            } else {
                Utils.imageUrlToDataUrl(rawUrl).then(dataUrl => {
                    cache[id] = cache[id] || {};
                    cache[id][key] = dataUrl;
                    resolve(dataUrl);
                });
            }
        });

        return from(promise);
    }
}
